import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import useDimensions from "../../hooks/useDimensions";
import useElementInView from "../../hooks/useElementInView";
import { HorizontalSeparator } from "../CoreUI";
import accessibilityProvider from "../../utils/accessibilityProvider";
import {
	Asset,
	ChevronLeftIcon,
	ChevronRightIcon,
	DotSwitcher,
	PaddedContainer,
	SPACING,
	Text,
	FONT_SIZE,
	FONT_COLOR,
} from "@disco/disco_core";
import { ESSENTIAL_WIDGET_BASELINE_H_PADDING } from "./EssentialWidgetCard";
import EssentialWidgetOffer from "./EssentialWidgetOffer";
import useEssentialWidgetOffer from "../../hooks/useEssentialWidgetOffer";
import useCustomization, {
	CUSTOMIZATION_DETAIL,
	CUSTOMIZATION_TYPE,
} from "../../hooks/useCustomization";
import { WIDGET_TYPE, WIDGET_VERSION } from "../../conf";
import useExperience from "../../hooks/useExperience";

import "./essential-widget-offers.css";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import { WIDGET_ELEMENT_CLICK_TARGET } from "../../event_conf";
import useMode from "../../hooks/useMode";

const EssentialWidgetOffersNavigator = ({ children }) => {
	const { inDesktopOnly } = useDimensions();
	const track = useTrack();
	const { isInline } = useMode();

	const {
		activeOfferIdx,
		isFirstOffer,
		showNextOffer,
		showPrevOffer,
		isLastOffer,

		TOTAL_OFFERS,

		defaultOfferEventProps,
	} = useEssentialWidgetOffer();

	const shouldShowNavArrows = !isInline && inDesktopOnly && !isFirstOffer;
	const shouldShowCarousel = !isFirstOffer;

	const handleNavForward = () => {
		if (isLastOffer) return;
		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			...defaultOfferEventProps,
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.EXPLICIT_NAV_FORWARD,
		});
		showNextOffer();
	};

	const handleNavBackward = () => {
		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			...defaultOfferEventProps,
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.EXPLICIT_NAV_BACKWARD,
		});
		showPrevOffer();
	};

	const renderNavArrow = (icon, handler) => (
		<Asset
			clickable={!!icon}
			style={{
				backgroundColor: "#C3C3C6",
				borderRadius: "200px",
				opacity: !icon ? 0 : 1,
				flexShrink: 0,
			}}
			size={36}
			vPadding={SPACING.TINY}
			hPadding={SPACING.TINY}
			onClick={handler}
		>
			{icon}
		</Asset>
	);

	return (
		<PaddedContainer flexContent={inDesktopOnly} alignContentCenter>
			{shouldShowNavArrows &&
				renderNavArrow(<ChevronLeftIcon />, handleNavBackward)}
			<PaddedContainer
				style={{ flexGrow: 1 }}
				hPadding={
					(shouldShowNavArrows || isInline) &&
					(isInline ? 16 : SPACING.REGULAR)
				}
			>
				{children}
				{shouldShowCarousel && (
					<PaddedContainer
						vPadding={isInline ? 16 : 10}
						{...(isInline && { tightTop: true })}
					>
						<DotSwitcher
							viewOnly
							className="essential-widget-offers-navigator-dot-switcher"
							dots={TOTAL_OFFERS}
							active={activeOfferIdx}
							hMarginAuto
						/>
					</PaddedContainer>
				)}
				{/* Carousel */}
			</PaddedContainer>
			{shouldShowNavArrows &&
				renderNavArrow(
					!isLastOffer ? <ChevronRightIcon /> : null,
					handleNavForward
				)}
		</PaddedContainer>
	);
};

const animationVariants = {
	initial: {
		opacity: 0,
	},
	animate: {
		opacity: 1,
		transition: {
			opacity: { duration: 0.3, ease: "easeInOut" },
		},
	},
	exit: {
		opacity: 0,
		transition: {
			opacity: { duration: 0.2, ease: "easeInOut" },
		},
	},
};

const UnrenderedOfferSlotHeadingContent = () => {
	const { applyCustomizationOrFallback } = useCustomization();
	const { isLargeWidget } = useDimensions();

	const COMPUTED_PADDING = isLargeWidget ? 20 : 16;

	const primaryHeading = applyCustomizationOrFallback(
		CUSTOMIZATION_DETAIL[CUSTOMIZATION_TYPE.CHECKOUT].WIDGET_HEADING,
		"Special offers just for you"
	);

	const secondaryHeading = applyCustomizationOrFallback(
		CUSTOMIZATION_DETAIL[CUSTOMIZATION_TYPE.CHECKOUT].WIDGET_SUBHEADING,
		"As a thank you for your recent purchase"
	);

	return (
		<PaddedContainer hPadding={COMPUTED_PADDING}>
			<Text
				size={FONT_SIZE.SUB_TITLE}
				className="lead-gen-header-title"
				{...accessibilityProvider({
					role: "heading",
					name: primaryHeading,
					level: 1,
				})}
			>
				{primaryHeading}
			</Text>
			<Text
				size={FONT_SIZE.BODY}
				color={FONT_COLOR.MID}
				{...accessibilityProvider({
					role: "heading",
					name: secondaryHeading,
					level: 2,
				})}
			>
				{secondaryHeading}
			</Text>
		</PaddedContainer>
	);
};

const EssentialWidgetOffers = ({ shouldRenderOfferSlot, triggerResize }) => {
	const { requestParentStats } = useElementInView();
	const { activeOfferIdx } = useEssentialWidgetOffer();
	const { isInline } = useMode();
	const { activeWidgetVersion } = useExperience();
	const isInitialMount = useRef(true);

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
		}
	}, []);

	const shouldDisableAnimation =
		activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.ESSENTIAL]["0.3"];

	const motionProps = shouldDisableAnimation
		? {
				initial: false,
				animate: { opacity: 1 },
				exit: { opacity: 0 },
				transition: { duration: 0 },
		  }
		: {
				initial: isInitialMount.current ? false : "initial",
				animate: "animate",
				exit: "exit",
				variants: animationVariants,
		  };

	return (
		<AnimatePresence
			mode="wait"
			onExitComplete={() => {
				triggerResize();
				requestParentStats();
			}}
		>
			{shouldRenderOfferSlot ? (
				<motion.div key="offers-slot" {...motionProps}>
					<PaddedContainer
						{...(!isInline && {
							hPadding: ESSENTIAL_WIDGET_BASELINE_H_PADDING,
							vPadding: 10,
						})}
					>
						<EssentialWidgetOffersNavigator>
							<EssentialWidgetOffer key={activeOfferIdx} />
							{/* Need a key to let the component state has changed */}
						</EssentialWidgetOffersNavigator>
					</PaddedContainer>
					<HorizontalSeparator />
				</motion.div>
			) : (
				<motion.div key="trending-offers-heading" {...motionProps}>
					<UnrenderedOfferSlotHeadingContent />
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default EssentialWidgetOffers;
